/**
 * Forge Memory - Global Statistics Manager
 * Manages aggregate statistics across all threads using localStorage
 * Tracks wins by category for personalized AI recommendations
 */

const STORAGE_KEY = 'neuroforge_global_stats';
const FORGE_MEMORY_KEY = 'neuroforge_forge_memory';

/**
 * Category definitions for automatic prompt classification
 */
const CATEGORY_PATTERNS = {
  coding: {
    name: 'Coding & Programming',
    icon: 'code',
    priority: 1, // Check first
    patterns: [
      /\b(code|coding|program|programming|function|method|class|variable|debug|bug|fix|error|compile|syntax|algorithm|api|sdk|library|framework|javascript|typescript|python|java|c\+\+|rust|go|ruby|php|html|css|react|vue|angular|node|npm|git|github|database|sql|mongodb|docker|kubernetes|aws|azure|gcp)\b/i,
      /\b(implement|refactor|optimize|write a? ?(?:script|function|class|method|program)|how (?:do i|to) (?:code|program|build|create|make))\b/i,
      /\b(script|scripts)\b.*\b(python|javascript|bash|shell|node|test|run|execute|api)\b/i,
      /\b(python|javascript|bash|shell|node|test|api)\b.*\b(script|scripts)\b/i
    ]
  },
  creative: {
    name: 'Creative Writing',
    icon: 'pen',
    priority: 2,
    patterns: [
      /\b(story|poem|essay|creative|fiction|narrative|character|plot|dialogue|screenplay|novel|blog|article|content|copywriting|slogan|tagline|marketing|ad copy)\b/i,
      /\b(write (?:me |a )?(?:story|poem|essay|article|blog|screenplay)|creative writing|brainstorm|imagine|create a (?:story|narrative|character))\b/i
    ]
  },
  analysis: {
    name: 'Analysis & Research',
    icon: 'search',
    patterns: [
      /\b(analyze|analysis|research|study|investigate|examine|compare|contrast|evaluate|assess|review|critique|pros and cons|advantages|disadvantages|benefits|drawbacks)\b/i,
      /\b(what are the|explain the|difference between|compare|how does .* work|why (?:is|does|do)|implications of)\b/i
    ]
  },
  math: {
    name: 'Math & Logic',
    icon: 'calculator',
    patterns: [
      /\b(math|mathematics|calculate|equation|formula|solve|proof|theorem|algebra|calculus|geometry|statistics|probability|number|arithmetic)\b/i,
      /\b(solve for|calculate|what is \d|how many|percentage|ratio|fraction|derivative|integral)\b/i
    ]
  },
  business: {
    name: 'Business & Strategy',
    icon: 'briefcase',
    patterns: [
      /\b(business|strategy|marketing|sales|revenue|profit|startup|entrepreneur|investor|investment|roi|kpi|market|competition|customer|client|pricing|monetiz|growth|scale)\b/i,
      /\b(business plan|market analysis|competitive advantage|value proposition|go-to-market|pitch deck|financial model)\b/i
    ]
  },
  general: {
    name: 'General Knowledge',
    icon: 'lightbulb',
    patterns: [] // Fallback category
  }
};

/**
 * Detect category from prompt text
 * @param {string} prompt - The user's prompt
 * @returns {string} Category key
 */
export const detectCategory = (prompt) => {
  if (!prompt) return 'general';

  const promptLower = prompt.toLowerCase();

  // Sort categories by priority (lower number = higher priority)
  const sortedCategories = Object.entries(CATEGORY_PATTERNS)
    .filter(([key]) => key !== 'general')
    .sort((a, b) => (a[1].priority || 99) - (b[1].priority || 99));

  // Check each category's patterns in priority order
  for (const [key, category] of sortedCategories) {
    for (const pattern of category.patterns) {
      if (pattern.test(promptLower)) {
        return key;
      }
    }
  }

  return 'general';
};

/**
 * Get category info by key
 * @param {string} categoryKey - Category key
 * @returns {Object} Category info with name and icon
 */
export const getCategoryInfo = (categoryKey) => {
  return CATEGORY_PATTERNS[categoryKey] || CATEGORY_PATTERNS.general;
};

/**
 * Get all category definitions
 * @returns {Object} All category patterns
 */
export const getAllCategories = () => CATEGORY_PATTERNS;

/**
 * Default stats structure for a single model
 */
const getDefaultModelStats = () => ({
  roundWins: 0,
  tournamentWins: 0,
  avgScore: 0,
  totalScore: 0,
  totalBattles: 0,
  winStreak: 0,
  bestWinStreak: 0,
  lastWinDate: null
});

/**
 * Default Forge Memory structure
 */
const getDefaultForgeMemory = () => ({
  global: {
    gpt: getDefaultModelStats(),
    claude: getDefaultModelStats(),
    gemini: getDefaultModelStats()
  },
  byCategory: {
    coding: { gpt: getDefaultModelStats(), claude: getDefaultModelStats(), gemini: getDefaultModelStats() },
    creative: { gpt: getDefaultModelStats(), claude: getDefaultModelStats(), gemini: getDefaultModelStats() },
    analysis: { gpt: getDefaultModelStats(), claude: getDefaultModelStats(), gemini: getDefaultModelStats() },
    math: { gpt: getDefaultModelStats(), claude: getDefaultModelStats(), gemini: getDefaultModelStats() },
    business: { gpt: getDefaultModelStats(), claude: getDefaultModelStats(), gemini: getDefaultModelStats() },
    general: { gpt: getDefaultModelStats(), claude: getDefaultModelStats(), gemini: getDefaultModelStats() }
  },
  totalTournaments: 0,
  totalRounds: 0,
  lastUpdated: null,
  firstRecordedDate: null
});

/**
 * Get global statistics from localStorage (legacy support)
 * @returns {Object} Global stats object with round wins and tournament wins per agent
 */
export const getGlobalStats = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('[GlobalStats] Failed to load global stats:', error);
  }

  return {
    gpt: { roundWins: 0, tournamentWins: 0 },
    claude: { roundWins: 0, tournamentWins: 0 },
    gemini: { roundWins: 0, tournamentWins: 0 }
  };
};

/**
 * Get full Forge Memory from localStorage
 * @returns {Object} Complete Forge Memory object
 */
export const getForgeMemory = () => {
  try {
    const stored = localStorage.getItem(FORGE_MEMORY_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure all categories exist (in case new ones were added)
      const defaultMemory = getDefaultForgeMemory();
      return {
        ...defaultMemory,
        ...parsed,
        byCategory: {
          ...defaultMemory.byCategory,
          ...parsed.byCategory
        }
      };
    }
  } catch (error) {
    console.error('[ForgeMemory] Failed to load Forge Memory:', error);
  }

  return getDefaultForgeMemory();
};

/**
 * Save Forge Memory to localStorage
 * @param {Object} memory - Forge Memory object to save
 */
export const saveForgeMemory = (memory) => {
  try {
    memory.lastUpdated = new Date().toISOString();
    localStorage.setItem(FORGE_MEMORY_KEY, JSON.stringify(memory));
  } catch (error) {
    console.error('[ForgeMemory] Failed to save Forge Memory:', error);
  }
};

/**
 * Save global statistics to localStorage (legacy support)
 * @param {Object} stats - Stats object to save
 */
export const saveGlobalStats = (stats) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  } catch (error) {
    console.error('[GlobalStats] Failed to save global stats:', error);
  }
};

/**
 * Calculate win rate percentage
 * @param {number} wins - Number of wins
 * @param {number} total - Total battles
 * @returns {number} Win rate as percentage
 */
const calculateWinRate = (wins, total) => {
  if (total === 0) return 0;
  return Math.round((wins / total) * 100);
};

/**
 * Recalculate complete Forge Memory from all threads
 * @param {Array} threads - Array of thread objects
 * @returns {Object} Calculated Forge Memory
 */
export const recalculateForgeMemory = (threads) => {
  const memory = getDefaultForgeMemory();

  if (!threads || threads.length === 0) {
    return memory;
  }

  // Track win streaks per model
  const winStreaks = { gpt: 0, claude: 0, gemini: 0 };
  let firstDate = null;

  // Iterate through all threads and their messages
  threads.forEach(thread => {
    if (!thread.messages || thread.messages.length === 0) return;

    thread.messages.forEach(exchange => {
      // Get winner and scores from analysis
      const winner = exchange.analysis?.winner;
      const scores = exchange.analysis?.scores || {};

      if (!winner) return;

      // Normalize winner key to lowercase
      const winnerKey = String(winner).toLowerCase();
      if (!['gpt', 'claude', 'gemini'].includes(winnerKey)) return;

      // Detect category from original prompt (not the display prompt with [ANALYSIS] prefix)
      const prompt = exchange.originalPrompt || exchange.prompt || '';
      const category = detectCategory(prompt);

      // Track dates
      const exchangeDate = exchange.timestamp || thread.updatedAt || thread.createdAt;
      if (exchangeDate) {
        if (!firstDate || new Date(exchangeDate) < new Date(firstDate)) {
          firstDate = exchangeDate;
        }
      }

      // Increment total rounds
      memory.totalRounds++;

      // Update global stats for all models that participated
      ['gpt', 'claude', 'gemini'].forEach(model => {
        // Scores can be stored as { gpt: { total: 75, ... } } or { gpt: 75 }
        const scoreData = scores[model];
        const modelScore = typeof scoreData === 'object' && scoreData !== null
          ? (scoreData.total || 0)
          : (typeof scoreData === 'number' ? scoreData : 0);

        if (modelScore > 0) {
          // Update global
          memory.global[model].totalScore += modelScore;
          memory.global[model].totalBattles++;
          memory.global[model].avgScore = Math.round(
            memory.global[model].totalScore / memory.global[model].totalBattles
          );

          // Update category
          if (memory.byCategory[category]) {
            memory.byCategory[category][model].totalScore += modelScore;
            memory.byCategory[category][model].totalBattles++;
            memory.byCategory[category][model].avgScore = Math.round(
              memory.byCategory[category][model].totalScore / memory.byCategory[category][model].totalBattles
            );
          }
        }
      });

      // Count round win for winner
      memory.global[winnerKey].roundWins++;
      if (memory.byCategory[category]) {
        memory.byCategory[category][winnerKey].roundWins++;
      }

      // Update win streaks
      winStreaks[winnerKey]++;
      if (winStreaks[winnerKey] > memory.global[winnerKey].bestWinStreak) {
        memory.global[winnerKey].bestWinStreak = winStreaks[winnerKey];
      }
      // Reset other models' streaks
      ['gpt', 'claude', 'gemini'].forEach(model => {
        if (model !== winnerKey) {
          winStreaks[model] = 0;
        }
      });

      // Check if this is a tournament win (2 models disqualified)
      const disqualifiedCount = (exchange.disqualifiedModels || []).length;
      if (disqualifiedCount === 2) {
        memory.totalTournaments++;
        memory.global[winnerKey].tournamentWins++;
        memory.global[winnerKey].lastWinDate = exchangeDate;

        if (memory.byCategory[category]) {
          memory.byCategory[category][winnerKey].tournamentWins++;
          memory.byCategory[category][winnerKey].lastWinDate = exchangeDate;
        }
      }
    });
  });

  // Set current win streaks
  memory.global.gpt.winStreak = winStreaks.gpt;
  memory.global.claude.winStreak = winStreaks.claude;
  memory.global.gemini.winStreak = winStreaks.gemini;

  // Set first recorded date
  memory.firstRecordedDate = firstDate;

  return memory;
};

/**
 * Get recommendation for a category based on win rates
 * @param {Object} categoryStats - Stats for a specific category
 * @returns {Object} Recommendation with model and confidence
 */
export const getRecommendation = (categoryStats) => {
  if (!categoryStats) return null;

  const models = ['gpt', 'claude', 'gemini'];
  const totalBattles = models.reduce((sum, m) => sum + (categoryStats[m]?.tournamentWins || 0), 0);

  if (totalBattles < 3) {
    return { model: null, confidence: 'low', reason: 'Not enough data' };
  }

  // Calculate win rates
  const winRates = {};
  models.forEach(model => {
    winRates[model] = calculateWinRate(
      categoryStats[model]?.tournamentWins || 0,
      totalBattles
    );
  });

  // Find the best model
  let bestModel = 'gpt';
  let bestRate = winRates.gpt;

  models.forEach(model => {
    if (winRates[model] > bestRate) {
      bestRate = winRates[model];
      bestModel = model;
    }
  });

  // Determine confidence level
  let confidence = 'low';
  if (totalBattles >= 10 && bestRate >= 60) {
    confidence = 'high';
  } else if (totalBattles >= 5 && bestRate >= 50) {
    confidence = 'medium';
  }

  // Check for dominance (one model clearly winning)
  const secondBest = Math.max(...models.filter(m => m !== bestModel).map(m => winRates[m]));
  const isDominant = bestRate - secondBest >= 20;

  return {
    model: bestModel,
    winRate: bestRate,
    confidence,
    isDominant,
    totalBattles,
    reason: isDominant
      ? `${bestModel.toUpperCase()} dominates with ${bestRate}% win rate`
      : `${bestModel.toUpperCase()} leads with ${bestRate}% win rate`
  };
};

/**
 * Get all recommendations by category
 * @param {Object} memory - Full Forge Memory object
 * @returns {Object} Recommendations per category
 */
export const getAllRecommendations = (memory) => {
  const recommendations = {};

  Object.keys(CATEGORY_PATTERNS).forEach(category => {
    if (memory.byCategory[category]) {
      recommendations[category] = getRecommendation(memory.byCategory[category]);
    }
  });

  return recommendations;
};

/**
 * Recalculate global statistics from all threads (legacy support)
 * @param {Array} threads - Array of thread objects
 * @returns {Object} Calculated global stats
 */
export const recalculateGlobalStats = (threads) => {
  const memory = recalculateForgeMemory(threads);

  // Return legacy format for backward compatibility
  return {
    gpt: {
      roundWins: memory.global.gpt.roundWins,
      tournamentWins: memory.global.gpt.tournamentWins
    },
    claude: {
      roundWins: memory.global.claude.roundWins,
      tournamentWins: memory.global.claude.tournamentWins
    },
    gemini: {
      roundWins: memory.global.gemini.roundWins,
      tournamentWins: memory.global.gemini.tournamentWins
    }
  };
};

/**
 * Clear global statistics
 */
export const clearGlobalStats = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('[GlobalStats] Failed to clear global stats:', error);
  }
};

/**
 * Clear Forge Memory
 */
export const clearForgeMemory = () => {
  try {
    localStorage.removeItem(FORGE_MEMORY_KEY);
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('[ForgeMemory] Failed to clear Forge Memory:', error);
  }
};

/**
 * Update global stats by recalculating from threads and saving
 * Also updates Forge Memory
 * @param {Array} threads - Array of thread objects
 * @returns {Object} Legacy global stats format
 */
export const updateGlobalStats = (threads) => {
  // Calculate full memory
  const memory = recalculateForgeMemory(threads);
  saveForgeMemory(memory);

  // Return legacy format for backward compatibility
  const stats = {
    gpt: {
      roundWins: memory.global.gpt.roundWins,
      tournamentWins: memory.global.gpt.tournamentWins
    },
    claude: {
      roundWins: memory.global.claude.roundWins,
      tournamentWins: memory.global.claude.tournamentWins
    },
    gemini: {
      roundWins: memory.global.gemini.roundWins,
      tournamentWins: memory.global.gemini.tournamentWins
    }
  };

  saveGlobalStats(stats);
  return stats;
};

/**
 * Update Forge Memory from threads
 * @param {Array} threads - Array of thread objects
 * @returns {Object} Full Forge Memory object
 */
export const updateForgeMemory = (threads) => {
  const memory = recalculateForgeMemory(threads);
  saveForgeMemory(memory);

  // Also update legacy stats for backward compatibility
  updateGlobalStats(threads);

  return memory;
};

/**
 * Export model name mapping for display
 */
export const MODEL_DISPLAY_NAMES = {
  gpt: 'GPT',
  claude: 'Claude',
  gemini: 'Gemini'
};

/**
 * Export model colors for consistency
 */
export const MODEL_COLORS = {
  gpt: { main: '#10b981', bg: 'bg-emerald-500', text: 'text-emerald-400', light: 'bg-emerald-500/20' },
  claude: { main: '#f97316', bg: 'bg-orange-500', text: 'text-orange-400', light: 'bg-orange-500/20' },
  gemini: { main: '#3b82f6', bg: 'bg-blue-500', text: 'text-blue-400', light: 'bg-blue-500/20' }
};
